import { supabase } from '../lib/supabase';

export const userService = {
  // Get all users (for admin)
  getAllUsers: async () => {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.order('created_at', { ascending: false });
      
      if (error) throw error;
      return { users: data, error: null };
    } catch (error) {
      return { users: [], error };
    }
  },

  // Get single user by ID
  getUserById: async (userId) => {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();
      
      if (error) throw error;
      return { user: data, error: null };
    } catch (error) {
      return { user: null, error };
    }
  },

  // Update user profile
  updateUser: async (userId, updates, currentUserId) => {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update({
          ...updates,
          updated_at: new Date()?.toISOString(),
          updated_by: currentUserId
        })?.eq('id', userId)?.select()?.single();
      
      if (error) throw error;
      return { user: data, error: null };
    } catch (error) {
      return { user: null, error };
    }
  },

  // Delete user (soft delete by deactivating)
  deactivateUser: async (userId, currentUserId) => {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update({
          is_active: false,
          updated_at: new Date()?.toISOString(),
          updated_by: currentUserId
        })?.eq('id', userId)?.select()?.single();
      
      if (error) throw error;
      return { user: data, error: null };
    } catch (error) {
      return { user: null, error };
    }
  },

  // Activate user
  activateUser: async (userId, currentUserId) => {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update({
          is_active: true,
          updated_at: new Date()?.toISOString(),
          updated_by: currentUserId
        })?.eq('id', userId)?.select()?.single();
      
      if (error) throw error;
      return { user: data, error: null };
    } catch (error) {
      return { user: null, error };
    }
  },

  // Get users by role
  getUsersByRole: async (role) => {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('role', role)?.eq('is_active', true)?.order('created_at', { ascending: false });
      
      if (error) throw error;
      return { users: data, error: null };
    } catch (error) {
      return { users: [], error };
    }
  },

  // Search users
  searchUsers: async (query) => {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.or(`username.ilike.%${query}%,email.ilike.%${query}%,full_name.ilike.%${query}%`)?.eq('is_active', true)?.order('created_at', { ascending: false })?.limit(20);
      
      if (error) throw error;
      return { users: data, error: null };
    } catch (error) {
      return { users: [], error };
    }
  },

  // Get user stats
  getUserStats: async () => {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('role')?.eq('is_active', true);
      
      if (error) throw error;
      
      const stats = data?.reduce((acc, user) => {
        acc[user?.role] = (acc?.[user?.role] || 0) + 1;
        acc.total = (acc?.total || 0) + 1;
        return acc;
      }, {});
      
      return { stats, error: null };
    } catch (error) {
      return { stats: {}, error };
    }
  },

  // Update profile
  updateProfile: async (userId, updates) => {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.update({
          ...updates,
          updated_at: new Date()?.toISOString(),
          updated_by: userId
        })
        ?.eq('id', userId)
        ?.select()
        ?.single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error updating profile:', error);
      return { data: null, error };
    }
  },

  // Upload user headshot
  uploadHeadshot: async (userId, file) => {
    try {
      // Validate file
      if (!file || !file?.type?.startsWith('image/')) {
        throw new Error('Please select a valid image file');
      }

      // Check file size (max 5MB)
      if (file?.size > 5242880) {
        throw new Error('Image size must be less than 5MB');
      }

      // Create unique file path
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${userId}-${Date.now()}.${fileExt}`;
      const filePath = `${userId}/${fileName}`;

      // Upload to Supabase storage (profile-images is public bucket)
      const { data: uploadData, error: uploadError } = await supabase?.storage
        ?.from('profile-images')
        ?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase?.storage
        ?.from('profile-images')
        ?.getPublicUrl(filePath);

      const publicUrl = urlData?.publicUrl;

      // Update user profile with new photo URL
      const { data: updateData, error: updateError } = await supabase
        ?.from('user_profiles')
        ?.update({
          profile_photo_url: publicUrl,
          updated_at: new Date()?.toISOString(),
          updated_by: userId
        })
        ?.eq('id', userId)
        ?.select()
        ?.single();

      if (updateError) throw updateError;

      return { user: updateData, photoUrl: publicUrl, error: null };
    } catch (error) {
      console.error('Error uploading headshot:', error);
      return { user: null, photoUrl: null, error };
    }
  },

  // Get user's favorite musicals
  getFavoriteMusicals: async (userId) => {
    try {
      const { data, error } = await supabase
        ?.from('user_musical_favorites')
        ?.select(`
          id,
          created_at,
          musicals (
            id,
            title,
            poster_url,
            description,
            genre,
            composer,
            year_premiered
          )
        `)
        ?.eq('user_id', userId)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      return { 
        favorites: (data || [])?.map(fav => ({
          favoriteId: fav?.id,
          musicalId: fav?.musicals?.id,
          title: fav?.musicals?.title,
          posterUrl: fav?.musicals?.poster_url,
          description: fav?.musicals?.description,
          genre: fav?.musicals?.genre,
          composer: fav?.musicals?.composer,
          yearPremiered: fav?.musicals?.year_premiered,
          addedAt: fav?.created_at
        })), 
        error: null 
      };
    } catch (error) {
      console.error('Error fetching favorite musicals:', error);
      return { favorites: [], error };
    }
  },

  // Add musical to favorites
  addFavoriteMusical: async (userId, musicalId) => {
    try {
      const { data, error } = await supabase
        ?.from('user_musical_favorites')
        ?.insert({
          user_id: userId,
          musical_id: musicalId
        })
        ?.select()
        ?.single();

      if (error) throw error;

      return { favorite: data, error: null };
    } catch (error) {
      console.error('Error adding favorite musical:', error);
      return { favorite: null, error };
    }
  },

  // Remove musical from favorites
  removeFavoriteMusical: async (userId, musicalId) => {
    try {
      const { error } = await supabase
        ?.from('user_musical_favorites')
        ?.delete()
        ?.eq('user_id', userId)
        ?.eq('musical_id', musicalId);

      if (error) throw error;

      return { error: null };
    } catch (error) {
      console.error('Error removing favorite musical:', error);
      return { error };
    }
  },

  // Check if musical is favorited by user
  isFavoriteMusical: async (userId, musicalId) => {
    try {
      const { data, error } = await supabase
        ?.from('user_musical_favorites')
        ?.select('id')
        ?.eq('user_id', userId)
        ?.eq('musical_id', musicalId)
        ?.maybeSingle();

      if (error) throw error;

      return { isFavorite: !!data, error: null };
    } catch (error) {
      console.error('Error checking favorite status:', error);
      return { isFavorite: false, error };
    }
  },

  // Get user's favorite songs
  getFavoriteSongs: async (userId) => {
    try {
      const { data, error } = await supabase
        ?.from('song_favorites')
        ?.select('id, song_id, created_at')
        ?.eq('user_id', userId);

      if (error) throw error;

      return {
        favorites: (data || [])?.map(fav => fav?.song_id),
        error: null
      };
    } catch (error) {
      console.error('Error fetching favorite songs:', error);
      return { favorites: [], error };
    }
  },

  // Add song to favorites
  addFavoriteSong: async (userId, songId) => {
    try {
      const { data, error } = await supabase
        ?.from('song_favorites')
        ?.insert({ user_id: userId, song_id: songId })
        ?.select()
        ?.single();

      if (error) throw error;
      return { favorite: data, error: null };
    } catch (error) {
      console.error('Error adding favorite song:', error);
      return { favorite: null, error };
    }
  },

  // Remove song from favorites
  removeFavoriteSong: async (userId, songId) => {
    try {
      const { error } = await supabase
        ?.from('song_favorites')
        ?.delete()
        ?.eq('user_id', userId)
        ?.eq('song_id', songId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error removing favorite song:', error);
      return { error };
    }
  },

  // Check if song is favorited by user
  isFavoriteSong: async (userId, songId) => {
    try {
      const { data, error } = await supabase
        ?.from('song_favorites')
        ?.select('id')
        ?.eq('user_id', userId)
        ?.eq('song_id', songId)
        ?.maybeSingle();

      if (error) throw error;
      return { isFavorite: !!data, error: null };
    } catch (error) {
      console.error('Error checking song favorite status:', error);
      return { isFavorite: false, error };
    }
  },

  // Get user's favorite roles
  getFavoriteRoles: async (userId) => {
    try {
      const { data, error } = await supabase
        ?.from('user_role_favorites')
        ?.select('id, role_id, created_at')
        ?.eq('user_id', userId);

      if (error) throw error;

      return {
        favorites: (data || [])?.map(fav => fav?.role_id),
        error: null
      };
    } catch (error) {
      console.error('Error fetching favorite roles:', error);
      return { favorites: [], error };
    }
  },

  // Add role to favorites
  addFavoriteRole: async (userId, roleId) => {
    try {
      const { data, error } = await supabase
        ?.from('user_role_favorites')
        ?.insert({ user_id: userId, role_id: roleId })
        ?.select()
        ?.single();

      if (error) throw error;
      return { favorite: data, error: null };
    } catch (error) {
      console.error('Error adding favorite role:', error);
      return { favorite: null, error };
    }
  },

  // Remove role from favorites
  removeFavoriteRole: async (userId, roleId) => {
    try {
      const { error } = await supabase
        ?.from('user_role_favorites')
        ?.delete()
        ?.eq('user_id', userId)
        ?.eq('role_id', roleId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error removing favorite role:', error);
      return { error };
    }
  },

  // Check if role is favorited by user
  isFavoriteRole: async (userId, roleId) => {
    try {
      const { data, error } = await supabase
        ?.from('user_role_favorites')
        ?.select('id')
        ?.eq('user_id', userId)
        ?.eq('role_id', roleId)
        ?.maybeSingle();

      if (error) throw error;
      return { isFavorite: !!data, error: null };
    } catch (error) {
      console.error('Error checking role favorite status:', error);
      return { isFavorite: false, error };
    }
  },

  // Get random musicals for onboarding (30 musicals)
  getRandomMusicals: async (limit = 30) => {
    try {
      // First, get total count
      const { count, error: countError } = await supabase
        ?.from('musicals')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('is_active', true);

      if (countError) throw countError;

      // Get random musicals using window function
      const { data, error } = await supabase
        ?.from('musicals')
        ?.select('id, title, poster_url, description, genre, composer, year_premiered')
        ?.eq('is_active', true)
        ?.order('created_at', { ascending: false })
        ?.limit(Math.min(limit, count || 100));

      if (error) throw error;

      // Shuffle array to get random selection
      const shuffled = (data || [])?.sort(() => 0.5 - Math.random());
      const selected = shuffled?.slice(0, limit);

      return { 
        musicals: selected?.map(m => ({
          id: m?.id,
          title: m?.title,
          posterUrl: m?.poster_url,
          description: m?.description,
          genre: m?.genre,
          composer: m?.composer,
          yearPremiered: m?.year_premiered
        })), 
        error: null 
      };
    } catch (error) {
      console.error('Error fetching random musicals:', error);
      return { musicals: [], error };
    }
  },

  // Check if user has completed onboarding (has favorite musicals)
  hasCompletedOnboarding: async (userId) => {
    try {
      // Check both the onboarding_completed flag and favorites count for robustness
      const { data: profile, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('onboarding_completed')
        ?.eq('id', userId)
        ?.single();

      if (profileError) throw profileError;

      // If explicitly marked as completed, return true
      if (profile?.onboarding_completed) {
        return { hasCompleted: true, favoritesCount: 0, error: null };
      }

      // Otherwise, check if they have favorites (fallback for users before migration)
      const { count, error } = await supabase
        ?.from('user_musical_favorites')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('user_id', userId);

      if (error) throw error;

      return { hasCompleted: (count || 0) > 0, favoritesCount: count || 0, error: null };
    } catch (error) {
      console.error('Error checking onboarding status:', error);
      return { hasCompleted: false, favoritesCount: 0, error };
    }
  },

  // Mark user's onboarding as completed
  markOnboardingCompleted: async (userId) => {
    try {
      const { error } = await supabase
        ?.rpc('mark_onboarding_completed', { user_uuid: userId });

      if (error) throw error;

      return { error: null };
    } catch (error) {
      console.error('Error marking onboarding as completed:', error);
      return { error };
    }
  },

  // Save favorite musicals (batch insert)
  async saveFavoriteMusicals(userId, musicalIds) {
    try {
      // First verify the user profile exists
      const { data: profile, error: profileError } = await supabase?.from('user_profiles')?.select('id')?.eq('id', userId)?.single();

      if (profileError) {
        console.error('User profile not found:', profileError);
        throw new Error('User profile not found. Please try logging in again.');
      }

      // Get the current authenticated user to ensure we're using the correct ID
      const { data: { user: authUser }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !authUser) {
        throw new Error('Authentication required. Please log in again.');
      }

      // Use the authenticated user's ID for RLS policy compliance
      const favorites = musicalIds?.map(musicalId => ({
        user_id: authUser?.id,
        musical_id: musicalId
      }));

      const { error } = await supabase?.from('user_musical_favorites')?.insert(favorites);

      if (error) {
        console.error('Error inserting favorites:', error);
        throw error;
      }

      return true;
    } catch (error) {
      console.error('Error in saveFavoriteMusicals:', error);
      throw error;
    }
  },

  // Add single favorite musical
  async addFavoriteMusical(userId, musicalId) {
    try {
      // Get the current authenticated user for RLS compliance
      const { data: { user: authUser }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !authUser) {
        throw new Error('Authentication required. Please log in again.');
      }

      const { error } = await supabase?.from('user_musical_favorites')?.insert({
          user_id: authUser?.id,
          musical_id: musicalId
        });

      if (error) {
        console.error('Error adding favorite:', error);
        throw error;
      }

      return true;
    } catch (error) {
      console.error('Error in addFavoriteMusical:', error);
      throw error;
    }
  },

  // Remove favorite musical
  async removeFavoriteMusical(userId, musicalId) {
    try {
      // Get the current authenticated user for RLS compliance
      const { data: { user: authUser }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !authUser) {
        throw new Error('Authentication required. Please log in again.');
      }

      const { error } = await supabase?.from('user_musical_favorites')?.delete()?.eq('user_id', authUser?.id)?.eq('musical_id', musicalId);

      if (error) {
        console.error('Error removing favorite:', error);
        throw error;
      }

      return true;
    } catch (error) {
      console.error('Error in removeFavoriteMusical:', error);
      throw error;
    }
  }
};

export default userService;